import { CheckCircle2, CircleHelp, MinusCircle, PackagePlus, PlusCircle, Printer, Trash2, TriangleAlert } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import { Button, useSwipeDismiss } from '@krp/ui-kit'

import { CommandStatus } from '../../../shared/ui/CommandStatus'
import { countProblem, countText, parseCount } from '../model/input'
import type { DeviationKind, InventoryRowVm } from '../model/types'

/** Отклонение — значком и словом, а не цветом (экран в `grayscale(1)` читаем). */
const DEVIATION_ICONS: Record<DeviationKind, ReactNode> = {
  surplus: <PlusCircle size={14} aria-hidden="true" />,
  shortage: <MinusCircle size={14} aria-hidden="true" />,
  match: <CheckCircle2 size={14} aria-hidden="true" />,
  none: null,
}

/**
 * Строка обхода. Удаляется жестом влево по шапке строки ИЛИ кнопкой «Убрать»
 * — жест никогда не единственный способ; без подтверждающего окна (решение
 * владельца 23.09.2026, карточка 264): строка исчезает на глазах рядом с
 * пересчитанным итогом.
 */
export function InventoryRowItem({
  row,
  disabled,
  onCount,
  onReprint,
  onRemove,
  onCreate,
  onResend,
  onDismiss,
}: {
  row: InventoryRowVm
  disabled: boolean
  onCount: (counted: number | null) => void
  onReprint: () => void
  onRemove: () => void
  onCreate: () => void
  onResend: (key: string) => void
  onDismiss: (key: string) => void
}) {
  const locked = disabled || !row.editable
  const swipe = useSwipeDismiss({ axis: 'left', onDismiss: onRemove, disabled: locked })
  const countId = useId()
  // Текст поля — свой: «1,» по дороге к «1,5» — ещё не число, но уже ввод.
  const [text, setText] = useState(() => countText(row.counted))
  // Количество сменилось не отсюда (сессия перечитана): поле показывает новое.
  const [shown, setShown] = useState(row.counted)
  if (shown !== row.counted) {
    setShown(row.counted)
    if (parseCount(text) !== row.counted) setText(countText(row.counted))
  }

  const onText = (value: string) => {
    setText(value)
    // Негодное значение не уходит в сессию: одна такая строка сорвала бы сохранение всего склада.
    if (countProblem(value) !== null) return
    onCount(parseCount(value))
  }
  const problem = countProblem(text)
  const invalid = problem !== null

  return (
    <li className="onec-inventory__row" data-found={row.found} style={swipe.style}>
      <div className="onec-inventory__grip" {...swipe.bind()}>
        {row.found ? null : <TriangleAlert size={16} aria-hidden="true" />}
        <span className="onec-inventory__name">{row.found ? row.name : `Штрихкод ${row.barcode} не найден в 1С`}</span>
        <span className="onec-inventory__barcode">{row.barcode}</span>
      </div>

      {row.found ? (
        <>
          <ul className="onec-inventory__prices" aria-label="Цены складов">
            {row.prices.map((price) => (
              <li key={price.warehouseRefKey}>
                {price.warehouseName}
                {price.selected ? ' (этот склад)' : ''}: <b>{price.text}</b>
              </li>
            ))}
          </ul>
          <div className="onec-inventory__counts">
            <span>
              Учёт: <b>{row.stockText}</b>
              {row.stockUnknown && (
                <span className="onec-inventory__hint">
                  {' '}
                  <CircleHelp size={14} aria-hidden="true" /> отклонение считается от нуля
                </span>
              )}
            </span>
            <div className="krp-field onec-inventory__count">
              <label className="krp-field__label" htmlFor={countId}>
                Посчитано
              </label>
              <input
                id={countId}
                inputMode="decimal"
                value={text}
                disabled={locked}
                aria-invalid={invalid || undefined}
                onChange={(event) => onText(event.target.value)}
              />
              {problem !== null && <span className="onec-field__note">{problem} — не сохранено</span>}
            </div>
            <span className="onec-inventory__deviation" data-kind={row.deviation.kind}>
              {DEVIATION_ICONS[row.deviation.kind]} {row.deviation.text}
            </span>
          </div>
        </>
      ) : (
        <p className="onec-inventory__missing">Товара с этим штрихкодом в этой базе нет — его можно создать.</p>
      )}

      {row.create !== null && row.createKey !== null && (
        <CommandStatus
          command={row.create}
          onResend={() => onResend(row.createKey ?? '')}
          onDismiss={() => onDismiss(row.createKey ?? '')}
        />
      )}

      <div className="onec-inventory__actions">
        {row.found ? (
          <Button
            variant={row.reprint ? 'primary' : 'secondary'}
            aria-pressed={row.reprint}
            icon={<Printer size={16} aria-hidden="true" />}
            disabled={locked}
            onClick={onReprint}
          >
            {row.reprint ? 'Ценник: перепечатать' : 'Ценник: верный'}
          </Button>
        ) : (
          <Button
            variant="primary"
            icon={<PackagePlus size={16} aria-hidden="true" />}
            disabled={locked}
            onClick={onCreate}
          >
            Создать товар
          </Button>
        )}
        <Button
          variant="ghost"
          icon={<Trash2 size={16} aria-hidden="true" />}
          disabled={locked}
          onClick={onRemove}
          aria-label={`Убрать строку ${row.found ? row.name : row.barcode}`}
        >
          Убрать
        </Button>
      </div>
    </li>
  )
}
